import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarPlus, CalendarDays, FolderOpen, MessageCircle, Bell, UserRound, LogOut } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/portal", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/book", icon: CalendarPlus, label: "Book" },
  { to: "/portal/bookings", icon: CalendarDays, label: "Bookings" },
  { to: "/portal/projects", icon: FolderOpen, label: "Projects" },
  { to: "/portal/messages", icon: MessageCircle, label: "Messages" },
  { to: "/portal/notifications", icon: Bell, label: "Notifications" },
  { to: "/portal/profile", icon: UserRound, label: "Profile" },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications/mine").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-lilac-mist flex" data-testid="client-portal">
      <aside className="w-16 lg:w-64 shrink-0 bg-obsidian text-white flex flex-col fixed inset-y-0 z-40 grain">
        <Link to="/" className="flex items-center justify-center lg:justify-start px-4 lg:px-5 h-16 border-b border-white/10" data-testid="portal-logo">
          <img src="/logo.jpg" alt="Mixed By Empress" className="w-10 h-10 rounded-full object-cover ring-1 ring-[#D4AF37]/50 shrink-0" />
        </Link>
        <nav className="flex-1 py-6 px-2 lg:px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`portal-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-colors ${
                  isActive ? "bg-white/10 text-gold" : "text-white/55 hover:text-white hover:bg-white/5"
                }`
              }>
              <n.icon size={17} className="shrink-0" />
              <span className="hidden lg:block">{n.label}</span>
              {n.label === "Notifications" && unread > 0 && (
                <span className="hidden lg:flex ml-auto w-5 h-5 rounded-full bg-gold text-obsidian text-[10px] font-bold items-center justify-center" data-testid="nav-unread-badge">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="w-9 h-9 rounded-full bg-purple-700 flex items-center justify-center text-sm font-semibold shrink-0">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </span>
            <div className="hidden lg:block min-w-0">
              <p className="text-sm truncate">{user?.name}</p>
              <p className="text-xs text-white/40 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={doLogout} data-testid="portal-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/50 hover:text-red-300 hover:bg-white/5 transition-colors">
            <LogOut size={15} className="shrink-0" /> <span className="hidden lg:block">Sign out</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-16 lg:ml-64 p-4 sm:p-8 lg:p-12 max-w-6xl">
        <Outlet />
      </main>
    </div>
  );
}
